import { useMemo, useState } from 'react'

import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { PageHeader } from '@/components/ui/Page'
import { EmptyState, ErrorState, Loading, Notice } from '@/components/ui/States'
import { cn } from '@/lib/cn'
import { formatAgo, today } from '@/lib/dates'
import { formatDistance, formatDuration, formatMoney, formatPhone, pluralise } from '@/lib/format'
import type { PricingRules } from '@/lib/pricing'
import { usePricingRules } from '@/features/quotes/queries'
import { describeError } from '@/lib/supabase'
import { unmeasuredCount, useArchiveVenue, useSaveVenue, useVenues, type Venue } from './queries'

/**
 * The venue library.
 *
 * This screen exists because the business chose not to take on a billed Google
 * Cloud project, so distance has to be measured once and kept. It earns its
 * place beyond that: everything a photographer needs to know before arriving —
 * parking, the coordinator's number, which room holds light — has somewhere to
 * live instead of being in someone's head or a WhatsApp thread from last March.
 *
 * The one number that must never be quietly missing is the distance, so
 * unmeasured venues are counted at the top and flagged in the row.
 */
export function VenuesPage() {
  const [showArchived, setShowArchived] = useState(false)
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<Venue | 'new' | null>(null)

  const query = useVenues(showArchived)
  const archive = useArchiveVenue()
  const rules = usePricingRules(today()).data?.rules

  const venues = query.data ?? []
  const unmeasured = unmeasuredCount(venues)

  // Keyed on the query data, not the `?? []` fallback, which is a new array
  // literal on every render and would re-filter on every keystroke regardless.
  const filtered = useMemo(() => {
    const all = query.data ?? []
    const needle = search.trim().toLowerCase()
    if (!needle) return all
    return all.filter((venue) =>
      [venue.name, venue.town, venue.formatted_address].some((field) => field?.toLowerCase().includes(needle)),
    )
  }, [query.data, search])

  if (editing) {
    return (
      <div className="pt-5">
        <VenueForm venue={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
      </div>
    )
  }

  return (
    <div className="pt-5">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <p className="text-[length:var(--text-sm)] text-taupe">
          {query.data ? `${pluralise(venues.length, 'venue')}, for quotes and invoices.` : 'For quotes and invoices.'}
        </p>
        <Button variant="primary" onClick={() => setEditing('new')}>
          Add a venue
        </Button>
      </div>

      {unmeasured > 0 ? (
        <Notice className="mb-4" tone="amber" title={`${pluralise(unmeasured, 'venue')} with no distance`}>
          A venue with no distance cannot price travel, so a quote for a wedding there will be missing a line. Open each
          one and add the one-way kilometres from home.
        </Notice>
      ) : null}

      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-b-[var(--border-hairline)] pb-3">
        <Field label="Search" className="w-full max-w-xs">
          <Input
            type="search"
            placeholder="Name, town or address"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Field>

        <Button variant="ghost" size="sm" onClick={() => setShowArchived(!showArchived)}>
          {showArchived ? 'Hide archived' : 'Show archived'}
        </Button>
      </div>

      {query.isPending ? (
        <Loading label="Loading venues" />
      ) : query.isError ? (
        <div className="pt-6">
          <ErrorState title="Venues could not load" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          className="mt-6"
          title={search ? 'Nothing matches that' : 'No venues yet'}
          body={
            search
              ? 'Try a shorter search, or add this one as a new venue.'
              : 'Add a venue once, with its distance from home, and every wedding there afterwards prices its own travel.'
          }
          action={search ? undefined : { label: 'Add a venue', onClick: () => setEditing('new') }}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="ledger mt-1 min-w-[58rem]">
            <thead>
              <tr>
                <th className="label">Venue</th>
                <th className="label">Town</th>
                <th className="label num">One way</th>
                <th className="label num">Drive</th>
                <th className="label num" title="Travel on a wedding quote, at today's rules">Wedding travel</th>
                <th className="label">Measured</th>
                <th className="label">Coordinator</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((venue) => (
                <tr key={venue.id} className={cn(venue.archived_at && 'opacity-50')}>
                  <td>
                    <button
                      type="button"
                      onClick={() => setEditing(venue)}
                      className="max-w-[16rem] truncate text-left underline decoration-transparent underline-offset-4 hover:decoration-[var(--color-rule-strong)]"
                    >
                      {venue.name}
                    </button>
                  </td>
                  <td className="text-taupe">{venue.town ?? '—'}</td>
                  <td className="num">
                    {venue.distance_km === null ? (
                      <Badge tone="clay">Not measured</Badge>
                    ) : (
                      formatDistance(Number(venue.distance_km))
                    )}
                  </td>
                  <td className="num text-taupe">{formatDuration(venue.travel_duration_minutes)}</td>
                  <td className="num" data-numeric>
                    {venue.distance_km === null || !rules ? '—' : weddingTravel(Number(venue.distance_km), rules)}
                  </td>
                  <td className="whitespace-nowrap text-taupe">
                    {venue.distance_measured_at ? formatAgo(venue.distance_measured_at) : '—'}
                  </td>
                  <td className="text-taupe">
                    {venue.coordinator_name ? (
                      <span className="block max-w-[12rem] truncate">
                        {venue.coordinator_name}
                        {venue.coordinator_phone ? ` · ${formatPhone(venue.coordinator_phone)}` : ''}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="num">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => archive.mutate({ id: venue.id, archived: !venue.archived_at })}
                    >
                      {venue.archived_at ? 'Restore' : 'Archive'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/**
 * What travel adds to a wedding quote at this venue, at today's rules: the
 * same arithmetic as the quote engine (distance beyond the included radius,
 * both ways, at the rate), shown so she can answer "what is travel to X?"
 * without building a quote.
 */
function weddingTravel(distanceKm: number, rules: PricingRules): string {
  const chargeable = Math.max(0, distanceKm - rules.freeTravelRadiusKm) * (rules.travelChargesReturnTrip ? 2 : 1)
  if (chargeable === 0) return 'Included'
  return formatMoney(Math.round(chargeable * rules.travelRatePerKm * 100) / 100)
}

function VenueForm({ venue, onDone }: { venue: Venue | null; onDone: () => void }) {
  const save = useSaveVenue()

  const [form, setForm] = useState({
    name: venue?.name ?? '',
    town: venue?.town ?? '',
    formatted_address: venue?.formatted_address ?? '',
    distance_km: venue?.distance_km === null || venue?.distance_km === undefined ? '' : String(venue.distance_km),
    travel_duration_minutes:
      venue?.travel_duration_minutes === null || venue?.travel_duration_minutes === undefined
        ? ''
        : String(venue.travel_duration_minutes),
    coordinator_name: venue?.coordinator_name ?? '',
    coordinator_phone: venue?.coordinator_phone ?? '',
    website_url: venue?.website_url ?? '',
    notes: venue?.notes ?? '',
  })

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const distance = form.distance_km.trim() === '' ? null : Number(form.distance_km)
  const duration = form.travel_duration_minutes.trim() === '' ? null : Number(form.travel_duration_minutes)

  const distanceInvalid = distance !== null && (!Number.isFinite(distance) || distance < 0)
  const durationInvalid = duration !== null && (!Number.isFinite(duration) || duration < 0)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (distanceInvalid || durationInvalid) return

    await save.mutateAsync({
      id: venue?.id,
      values: {
        name: form.name.trim(),
        town: form.town.trim() || null,
        formatted_address: form.formatted_address.trim() || null,
        distance_km: distance,
        travel_duration_minutes: duration === null ? null : Math.round(duration),
        coordinator_name: form.coordinator_name.trim() || null,
        coordinator_phone: form.coordinator_phone.trim() || null,
        website_url: form.website_url.trim() || null,
        notes: form.notes.trim() || null,
        distance_source: 'manual',
      },
    })

    onDone()
  }

  return (
    <form onSubmit={(event) => void submit(event)}>
      <PageHeader
        eyebrow="Venues"
        title={venue ? venue.name : 'Add a venue'}
        actions={
          <>
            <Button variant="ghost" onClick={onDone}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={save.isPending} loadingLabel="Saving" disabled={!form.name.trim()}>
              {venue ? 'Save venue' : 'Add venue'}
            </Button>
          </>
        }
      />

      <div className="grid gap-x-10 gap-y-8 border-t border-t-[var(--border-hairline)] pt-6 lg:grid-cols-2">
        <div className="flex flex-col gap-5">
          <p className="label">Where it is</p>

          <Field label="Name" required hint="What you and the couple call it.">
            <Input value={form.name} onChange={(event) => set('name', event.target.value)} required autoFocus />
          </Field>

          <Field label="Town" hint="Shown on the pipeline card.">
            <Input value={form.town} onChange={(event) => set('town', event.target.value)} />
          </Field>

          <Field label="Full address">
            <Input value={form.formatted_address} onChange={(event) => set('formatted_address', event.target.value)} />
          </Field>

          <Field label="Website">
            <Input type="url" value={form.website_url} onChange={(event) => set('website_url', event.target.value)} />
          </Field>
        </div>

        <div className="flex flex-col gap-5">
          <p className="label">Getting there</p>

          <Field
            label="One-way distance"
            aside="km"
            error={distanceInvalid ? 'Enter a distance of zero or more.' : undefined}
            hint="Road distance from Somerset West. The quote doubles it for the return leg."
          >
            <Input
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0"
              value={form.distance_km}
              onChange={(event) => set('distance_km', event.target.value)}
            />
          </Field>

          <Field
            label="Driving time"
            aside="minutes"
            error={durationInvalid ? 'Enter a time of zero or more.' : undefined}
            hint="One way. Used to work out how early to leave, not to price anything."
          >
            <Input
              type="number"
              inputMode="numeric"
              step="5"
              min="0"
              value={form.travel_duration_minutes}
              onChange={(event) => set('travel_duration_minutes', event.target.value)}
            />
          </Field>

          <Field label="Coordinator">
            <Input value={form.coordinator_name} onChange={(event) => set('coordinator_name', event.target.value)} />
          </Field>

          <Field label="Coordinator's phone">
            <Input type="tel" value={form.coordinator_phone} onChange={(event) => set('coordinator_phone', event.target.value)} />
          </Field>
        </div>

        <div className="lg:col-span-2">
          <Field
            label="Notes"
            hint="Where to park, load-in, which rooms hold light and when, anything restricted. The things you wish you had written down last time."
          >
            <Textarea rows={5} value={form.notes} onChange={(event) => set('notes', event.target.value)} />
          </Field>
        </div>
      </div>
    </form>
  )
}
